import { gql, useQuery } from "@apollo/client";
import { Button, Card, Col, Divider, Layout, Menu, Row, Tag } from "antd";
import Text from "antd/lib/typography/Text";
import Title from "antd/lib/typography/Title";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import React, { Fragment } from "react";
import { NavLink, useParams } from "react-router-dom";

const accountUserQuery = gql`
  query($id: ID!) {
    accountUser(id: $id) {
      id
      code
      email
      role
      language
      priceList {
        id
        code
      }
      paymentMethod {
        id
        code
        nameIt
      }
      companyName
      address
      zip
      city
      province
      country
      vatNumber
      userBrands {
        id
        brand {
          id
          code
        }
        salesperson {
          id
          code
          email
        }
      }
    }
    collectionBrands {
      nodes {
        id
        code
      }
    }
  }
`;

export const Show: React.FC<{}> = () => {
  const { id } = useParams();

  const { error, loading: queryLoading, data } = useQuery(accountUserQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="../">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const user = data.accountUser;

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Title level={4} style={{ lineHeight: "auto" }}>
                User: {user.code}
              </Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border no-margin">
              <NavLink to="../">
                <Button>Back</Button>
              </NavLink>
            </Menu.Item>
            <Menu.Item className="no-border">
              <NavLink to="./edit">
                <Button type="primary">Edit</Button>
              </NavLink>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            <Row gutter={[25, 30]}>
              <Col span={8}>
                <Title level={5}>Code</Title>
                <Text>{user.code}</Text>
              </Col>
              <Col span={8}>
                <Title level={5}>Email</Title>
                <Text>{user.email}</Text>
              </Col>
              <Col span={4}>
                <Title level={5}>Role</Title>
                <Text>{user.role}</Text>
              </Col>
              <Col span={4}>
                <Title level={5}>Language</Title>
                <Text>{user.language}</Text>
              </Col>
              <Col span={8}>
                <Title level={5}>Price List</Title>
                <Text>{user.priceList?.code}</Text>
              </Col>
              <Col span={16}>
                <Title level={5}>Payment Method</Title>
                <Text>
                  {user.paymentMethod?.code} - {user.paymentMethod?.nameIt}
                </Text>
              </Col>
              <Col span={8}>
                <Title level={5}>Company Name</Title>
                <Text>{user.companyName}</Text>
              </Col>
              <Col span={8}>
                <Title level={5}>Address</Title>
                <Text>{user.address}</Text>
              </Col>
              <Col span={8}>
                <Title level={5}>Zip</Title>
                <Text>{user.zip}</Text>
              </Col>
              <Col span={8}>
                <Title level={5}>City</Title>
                <Text>{user.city}</Text>
              </Col>
              <Col span={4}>
                <Title level={5}>Province</Title>
                <Text>{user.province}</Text>
              </Col>
              <Col span={4}>
                <Title level={5}>Country</Title>
                <Text>{user.country}</Text>
              </Col>
              <Col span={8}>
                <Title level={5}>Vat Number</Title>
                <Text>{user.vatNumber}</Text>
              </Col>
            </Row>
            <Divider />
            <Row gutter={[20, 20]} justify="space-between">
              <Col>
                <Title level={5}>User Brands</Title>
              </Col>
              {user.userBrands?.length < data.collectionBrands?.nodes.length && (
                <Col>
                  <NavLink to={`../../user_brands/new/${id}`}>
                    <Button type="primary">New Brand for User</Button>
                  </NavLink>
                </Col>
              )}
            </Row>
            <Row gutter={[25, 20]}>
              {user.userBrands?.map((ub: any, i: any) => (
                <Col span={12} key={i}>
                  <Card
                    type="inner"
                    title={
                      ub.brand.code === "palto" ? (
                        <Tag>{ub.brand.code.toUpperCase()}</Tag>
                      ) : (
                        <Tag color="volcano">{ub.brand.code.toUpperCase()}</Tag>
                      )
                    }
                    extra={<NavLink to={`../../user_brands/${ub.id}/edit/${id}`}>Edit</NavLink>}
                  >
                    {ub.salesperson ? `${ub.salesperson.code} - ${ub.salesperson.email}` : "No Salesperson"}
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
